import { Link, useNavigate } from 'react-router-dom';
import { LayoutHeader as Header } from '@/layouts';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Car,
  Shield,
  ArrowLeft,
  Heart,
  Share2,
  Calendar,
  Gauge,
  Fuel,
  Settings,
  Palette,
  Building2,
  MapPin,
  Phone,
  MessageCircle,
  Navigation,
  Eye
} from 'lucide-react';

interface VehicleNotFoundProps {
  config: any;
  buttonColor: string;
  primaryColor: string;
  secondaryColor: string;
  titleColor: string;
  textColor: string;
}

export const VehicleNotFound = ({
  config,
  buttonColor,
  primaryColor,
  secondaryColor,
  titleColor,
  textColor
}: VehicleNotFoundProps) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-muted/30">
      <Header config={config} />

      {/* Image Gallery Placeholder - Full Width at Top */}
      <section className="py-0 mt-[95px] md:mt-[105px] relative z-10">
        <div className="w-full">
          <div className="relative h-[auto]">
            {/* Mobile View - Single Image Placeholder */}
            <div className="md:hidden relative overflow-hidden">
              <div className="aspect-[4/3] bg-muted/30 animate-pulse relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/10 to-transparent animate-shimmer"></div>
              </div>
            </div>

            {/* Desktop View - 3 Images Placeholder */}
            <div className="hidden md:block relative overflow-hidden">
              <div className="h-[340px] flex gap-0">
                {/* Left placeholder */}
                <div className="w-1/3 h-full bg-muted/20 animate-pulse relative overflow-hidden opacity-60">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/10 to-transparent animate-shimmer"></div>
                </div>
                {/* Center placeholder */}
                <div className="w-1/3 h-full bg-muted/30 animate-pulse relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/10 to-transparent animate-shimmer" style={{ animationDelay: '0.2s' }}></div>
                </div>
                {/* Right placeholder */}
                <div className="w-1/3 h-full bg-muted/20 animate-pulse relative overflow-hidden opacity-60">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/10 to-transparent animate-shimmer" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-8 relative z-30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Vehicle Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Main Vehicle Info Card */}
              <Card>
                <CardContent className="p-0">
                  {/* Title Section */}
                  <div className="py-6 border-b mx-8">
                    <div className="h-8 bg-muted/50 rounded w-3/4 mb-2 animate-pulse" />
                    <div className="h-5 bg-muted/40 rounded w-1/2 animate-pulse" />
                  </div>

                  {/* Specifications Grid */}
                  <div className="p-8 pt-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                      {/* Ano */}
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          <Calendar className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Ano</p>
                          <div className="h-5 bg-muted/40 rounded w-20 mt-1 animate-pulse" />
                        </div>
                      </div>

                      {/* KM */}
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          <Gauge className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">KM</p>
                          <div className="h-5 bg-muted/40 rounded w-24 mt-1 animate-pulse" />
                        </div>
                      </div>

                      {/* Combustível */}
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          <Fuel className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Combustível</p>
                          <div className="h-5 bg-muted/40 rounded w-20 mt-1 animate-pulse" />
                        </div>
                      </div>

                      {/* Câmbio */}
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          <Settings className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Câmbio</p>
                          <div className="h-5 bg-muted/40 rounded w-24 mt-1 animate-pulse" />
                        </div>
                      </div>

                      {/* Cor */}
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          <Palette className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Cor</p>
                          <div className="h-5 bg-muted/40 rounded w-20 mt-1 animate-pulse" />
                        </div>
                      </div>

                      {/* Portas */}
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          <Car className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Portas</p>
                          <div className="h-5 bg-muted/40 rounded w-12 mt-1 animate-pulse" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Optionals Placeholder - Inside Main Card */}
                  <div className="border-t mx-8">
                    <div className="p-0 m-0">
                      <CardHeader className="p-0 pt-6 m-0">
                        <div className="flex items-center">
                          <div className="flex items-center gap-2">
                            <div className="w-1 h-6 rounded-full animate-pulse" style={{ background: primaryColor }}></div>
                            <div className="h-6 bg-muted/50 rounded w-24 animate-pulse" />
                          </div>
                          <Badge variant="secondary" className="text-sm font-semibold ml-4">
                            <div className="h-4 w-6 bg-muted/50 rounded animate-pulse" />
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="p-0 pb-6 pt-4 m-0">
                        <div className="flex flex-wrap gap-2">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                            <div key={i} className="h-7 bg-muted/40 rounded-full px-4 w-28 animate-pulse" />
                          ))}
                        </div>
                      </CardContent>
                    </div>
                  </div>

                  {/* Warning Placeholder - Inside Main Card */}
                  <div className="p-8">
                    <Card className="border-yellow-200 bg-yellow-50/50">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-0.5">
                            <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <div className="h-5 bg-yellow-900/20 rounded w-20 mb-2 animate-pulse" />
                            <div className="h-4 bg-yellow-800/20 rounded w-full animate-pulse" />
                            <div className="h-4 bg-yellow-800/20 rounded w-5/6 mt-1 animate-pulse" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>

              {/* Financing Card Placeholder */}
              <Card className="border-primary/20">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-6 bg-muted/50 rounded w-48 animate-pulse" />
                    </div>
                    <div className="h-12 bg-muted/30 rounded animate-pulse" />
                    <div className="h-12 bg-muted/30 rounded animate-pulse" />
                    <div className="h-12 bg-muted/30 rounded animate-pulse" />
                    <div className="h-12 bg-muted/30 rounded animate-pulse" />
                    <div className="h-12 bg-primary/20 rounded animate-pulse" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Alert Card - Veículo Não Disponível */}
              <Card className="border-orange-500/50 bg-orange-500/5">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <Shield className="h-6 w-6 text-orange-500" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-orange-600 mb-2">
                        Veículo Não Disponível
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Este veículo não está mais disponível em nosso estoque. Ele pode ter sido vendido recentemente ou o link pode estar incorreto.
                      </p>
                      <Link to="/estoque" className="w-full inline-block">
                        <Button
                          className="text-white w-full"
                          style={{ background: buttonColor }}>
                          <ArrowLeft className="h-4 w-4 mr-2" />
                          Ver Estoque Completo
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Guarantee Card */}
              <Card style={{ borderColor: secondaryColor }}>
                <CardContent className="p-6 text-center">
                  <div className="inline-flex p-3 rounded-full mb-3" style={{ backgroundColor: `${primaryColor}15` }}>
                    <Shield className="h-7 w-7" style={{ color: primaryColor }} />
                  </div>
                  <div className="h-5 bg-muted/50 rounded w-2/3 mx-auto mb-2 animate-pulse" />
                  <div className="h-4 bg-muted/40 rounded w-full animate-pulse" />
                  <div className="h-4 bg-muted/40 rounded w-5/6 mx-auto mt-1 animate-pulse" />
                </CardContent>
              </Card>

              {/* Test-Drive Placeholder */}
              <Card>
                <CardContent className="p-6">
                  <div className="mb-4">
                    <div className="h-6 bg-muted/50 rounded w-32 mb-2 animate-pulse" />
                    <div className="h-4 bg-muted/40 rounded w-full animate-pulse" />
                  </div>
                  <div className="h-12 bg-muted/30 rounded animate-pulse" />
                </CardContent>
              </Card>

              {/* Store Info Card */}
              <Card className="border border-border/50 shadow-sm">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="h-6 bg-muted/50 rounded w-2/3 mb-3 animate-pulse" />
                    
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                        <div className="flex-1">
                          <div className="h-4 bg-muted/40 rounded w-full animate-pulse" />
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground mt-1" />
                        <div className="flex-1">
                          <div className="h-4 bg-muted/40 rounded w-32 animate-pulse" />
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-2">
                      <div className="h-10 bg-green-600/20 rounded animate-pulse" />
                      <div className="h-10 bg-blue-600/20 rounded animate-pulse" />
                      <div className="h-10 bg-muted/30 rounded animate-pulse" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Related Vehicles Placeholder */}
          <div className="mt-16">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-foreground">Veículos Relacionados</h2>
            </div>

            <div className="relative group/carousel">
              <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide scroll-smooth pb-4">
                {/* 5 Vehicle Placeholders */}
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex-shrink-0 w-[260px] md:w-[260px] snap-start">
                    <Card className="overflow-hidden border-dashed border-border/50 bg-muted/20 flex flex-col h-full">
                      {/* Placeholder Image - Same aspect ratio as VehicleCard */}
                      <div className="relative aspect-[4/2.5] overflow-hidden flex-shrink-0 bg-muted/30 flex items-center justify-center">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/10 to-transparent animate-shimmer"></div>
                      </div>

                      {/* Compact Info Section - Match VehicleCard structure */}
                      <CardContent className="p-4 flex flex-col">
                        {/* Title and Version - Fixed heights */}
                        <div className="mb-3 flex-shrink-0">
                          <div className="h-6 bg-muted/50 rounded w-3/4 mb-2 animate-pulse"></div>
                          <div className="h-4 bg-muted/30 rounded w-1/2 mb-2 animate-pulse"></div>
                          {/* Price Skeleton */}
                          <div className="h-7 bg-muted/50 rounded w-2/3 animate-pulse"></div>
                        </div>

                        {/* Details - Single Row with fixed height */}
                        <div className="flex items-center gap-2 border-t border-border/50 pt-3 flex-shrink-0 h-8">
                          <div className="h-3 bg-muted/30 rounded w-16 animate-pulse"></div>
                          <div className="w-1 h-1 bg-muted/30 rounded-full"></div>
                          <div className="h-3 bg-muted/30 rounded w-12 animate-pulse"></div>
                        </div>

                        {/* CTA Space */}
                        <div className="h-4 mt-3 flex-shrink-0"></div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer config={config} />
    </div>
  );
};







